import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';

const SaleDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { getSaleById, getCustomers, getProducts } = useAppData();
  const [sale, setSale] = useState(null);
  const customers = getCustomers();
  const products = getProducts();

  useEffect(() => {
    if (location.state && location.state.sale) {
      setSale(location.state.sale);
      return;
    }
    const s = getSaleById(id);
    if (s) {
      setSale(s);
      return;
    }
    // Fallback: try localStorage
    const raw = localStorage.getItem('stockpro_sales') || localStorage.getItem('sales');
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        const found = parsed.find((x) => x.id === id || x.invoiceNumber === id);
        setSale(found || null);
      } catch {
        setSale(null);
      }
    }
  }, [id, getSaleById, location.state]);

  const getEntityName = (ent) => {
    if (!ent) return '';
    const full = `${ent.firstName || ''} ${ent.lastName || ''}`.trim();
    if (full) return full;
    if (ent.name && ent.name.trim() !== '') return ent.name;
    if (ent.company && ent.company.trim() !== '') return ent.company;
    return String(ent.id || '');
  };

  const formatCurrency = (value) => `${Number(value || 0).toLocaleString()} DA`;

  const resolvedItems = useMemo(() => {
    if (!sale || !Array.isArray(sale.items)) return [];
    return sale.items.map((item) => {
      const prod = products.find((p) => p.id === item.id || p.sku === item.sku);
      const price = Number(item.price ?? prod?.price ?? 0);
      const quantity = Number(item.quantity ?? 0);
      return {
        ...item,
        name: item.name || prod?.name || item.id,
        sku: item.sku || prod?.sku || item.id,
        price,
        quantity,
        total: Number(item.total ?? (price * quantity)),
      };
    });
  }, [sale, products]);

  const totals = useMemo(() => {
    const subtotal = resolvedItems.reduce((sum, it) => sum + (Number(it.total) || 0), 0);
    const discountPercent = Number(sale?.discount ?? 0);
    const discountAmount = (discountPercent / 100) * subtotal;
    const taxable = subtotal - discountAmount;
    const taxPercent = Number(sale?.tax ?? 0);
    const taxAmount = (taxPercent / 100) * taxable;
    const total = taxable + taxAmount;
    return { subtotal, discountAmount, taxable, taxAmount, total };
  }, [sale, resolvedItems]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/sales')}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6 text-white" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">Détails de la Vente</h1>
            <p className="text-white/60 mt-1">{id}</p>
          </div>
        </div>
      </div>

      {sale ? (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="text-white/60 text-sm">Facture</div>
              <div className="text-white font-semibold">{sale.invoiceNumber || sale.id}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="text-white/60 text-sm">Date</div>
              <div className="text-white font-semibold">{sale.date}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="text-white/60 text-sm">Client</div>
              <div className="text-white font-semibold">{getEntityName(customers.find(c=>c.id===sale.customerId)) || sale.customerName}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="text-white/60 text-sm">Total</div>
              <div className="text-white font-semibold">{formatCurrency(totals.total)}</div>
            </div>
          </div>

          {/* Items table */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Article</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Référence</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Prix</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Qté</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {resolvedItems.map((it, idx) => (
                    <tr key={`${it.id}-${idx}`} className="hover:bg-white/5">
                      <td className="px-6 py-4 text-white">{it.name}</td>
                      <td className="px-6 py-4 text-white/80">{it.sku}</td>
                      <td className="px-6 py-4 text-right text-white/80">{formatCurrency(it.price)}</td>
                      <td className="px-6 py-4 text-right text-white/80">{it.quantity}</td>
                      <td className="px-6 py-4 text-right text-white">{formatCurrency(it.total)}</td>
                    </tr>
                  ))}
                  {resolvedItems.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-white/60">
                        Aucun article dans cette vente.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 border-t border-white/10 flex justify-end">
              <div className="text-right space-y-1">
                <div className="text-white/70 text-sm">Sous-total: <span className="text-white font-medium">{formatCurrency(totals.subtotal)}</span></div>
                {Number(sale?.discount || 0) > 0 && (
                  <div className="text-white/70 text-sm">Remise ({sale.discount}%): <span className="text-white font-medium">-{formatCurrency(totals.discountAmount)}</span></div>
                )}
                <div className="text-white/70 text-sm">TVA ({sale?.tax ?? 0}%): <span className="text-white font-medium">{formatCurrency(totals.taxAmount)}</span></div>
                <div className="text-white text-lg font-semibold">Total: {formatCurrency(totals.total)}</div>
              </div>
            </div>
          </div>

          {sale?.notes && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="text-white/80 whitespace-pre-wrap">{sale.notes}</div>
            </div>
          )}
        </>
      ) : (
        <div className="p-6 bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 text-white/60">Vente introuvable ou non chargée.</div>
      )}
    </div>
  );
};

export default SaleDetail;

